import React, { useEffect, useState, useRef } from 'react';
import { api } from '@/lib/api';
import { Loader2, CheckCircle2, LogOut, HardDrive, AlertTriangle } from 'lucide-react';

export const GoogleDriveButton: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [user, setUser] = useState<{ email: string; name: string; picture?: string } | null>(null);
  const [mode, setMode] = useState<string>('none');
  const [menuOpen, setMenuOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    checkStatus();

    // Cerrar menú al hacer clic afuera
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const checkStatus = async () => {
    try {
      setLoading(true);
      const res = await api.getGoogleDriveStatus();
      setConnected(res.connected);
      setUser(res.user);
      setMode(res.mode);
    } catch (err) {
      console.warn('No se pudo verificar el estado de Google Drive:', err);
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      setConnecting(true);
      setErrorMessage(null);
      const redirectUri = window.location.origin + '/auth/callback';
      const res = await api.getGoogleDriveUrl(redirectUri);
      
      if (res.url) {
        window.location.href = res.url;
      }
    } catch (err: any) {
      console.error('Error al iniciar conexión de Google Drive:', err);
      setErrorMessage(
        err.message ||
          'Configura GOOGLE_CLIENT_ID en el backend para habilitar la conexión con un clic.'
      );
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('¿Seguro que deseas desconectar la cuenta de Google Drive del equipo?')) return;
    try {
      await api.disconnectGoogleDrive();
      setConnected(false);
      setUser(null);
      setMode('none');
      setMenuOpen(false);
    } catch (err) {
      console.error('Error al desconectar Google Drive:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border bg-card/60 text-xs text-muted-foreground">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        <span>Drive...</span>
      </div>
    );
  }

  return (
    <div className="relative" ref={menuRef}>
      {connected ? (
        <div>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-200/60 bg-emerald-50/50 hover:bg-emerald-50 text-emerald-800 text-xs font-medium transition-all shadow-sm"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <HardDrive className="w-3.5 h-3.5 text-emerald-600" />
            <span className="hidden sm:inline">Drive compartido</span>
            {user?.name && <span className="font-semibold text-emerald-900 truncate max-w-[120px]">{user.name.split(' ')[0]}</span>}
          </button>

          {menuOpen && (
            <div className="absolute right-0 mt-2 w-72 rounded-xl bg-white border border-slate-200/90 shadow-xl p-3.5 z-50 animate-in fade-in slide-in-from-top-1 text-slate-800">
              <div className="flex items-center gap-2.5 pb-2.5 mb-2.5 border-b border-slate-100">
                <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-xs shrink-0">
                  {user?.name?.charAt(0) || 'D'}
                </div>
                <div className="overflow-hidden min-w-0">
                  <p className="text-xs font-semibold text-slate-900 truncate">{user?.name || 'Google Drive'}</p>
                  <p className="text-[11px] text-slate-500 truncate">{user?.email || 'Cuenta conectada'}</p>
                </div>
              </div>

              <div className="p-2 rounded-lg bg-emerald-50/70 border border-emerald-100 text-[11px] text-emerald-900 mb-3 leading-relaxed">
                <div className="flex items-center gap-1.5 font-semibold text-emerald-950 mb-0.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  <span>Drive del equipo activo</span>
                </div>
                Esta cuenta almacena los entregables y carpetas de clientes. Es compartida y no se desconectará al cambiar de usuario.
              </div>

              {mode === 'oauth' && (
                <div className="space-y-1.5">
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      handleConnect();
                    }}
                    className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-700 hover:bg-slate-100 border border-slate-200 transition-colors"
                  >
                    <HardDrive className="w-3 h-3 text-slate-500" />
                    <span>Cambiar cuenta de Drive</span>
                  </button>

                  <button
                    onClick={handleDisconnect}
                    className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-rose-600 hover:bg-rose-50 border border-rose-100 transition-colors"
                  >
                    <LogOut className="w-3 h-3" />
                    <span>Desconectar Drive</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-end">
          <button
            onClick={handleConnect}
            disabled={connecting}
            className="flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-slate-200 bg-white hover:bg-slate-50 active:scale-[0.98] text-slate-700 text-xs font-medium shadow-sm transition-all"
            title="Conectar tu Google Drive para crear carpetas de clientes automáticamente"
          >
            {connecting ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
                <span>Abriendo Google...</span>
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                  />
                </svg>
                <span>Conectar Google Drive</span>
              </>
            )}
          </button>

          {errorMessage && (
            <div className="absolute right-0 top-10 mt-1 w-72 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-amber-900 text-[11px] shadow-lg z-50 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Faltan credenciales de Google OAuth</p>
                <p className="text-[10px] text-amber-700 mt-0.5 leading-snug">{errorMessage}</p>
                <button
                  onClick={() => setErrorMessage(null)}
                  className="mt-1 text-[10px] text-amber-900 underline font-medium"
                >
                  Entendido
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
